import { EmptyState, MMButton, PAGE_FILL, PayoutIcon, RecurringPaymentsIcon, TableCard } from '@components'
import cn from '@integrations/classnames'
import { useContext, useEffect, useState } from '@integrations/react'
import { useNavigate } from '@integrations/routing'
import { paths, useMerchantAccounts, useMyAccountAccessQry, useRecurringPlansQry } from 'mmfintech-backend-api'
import type { PortalRecurringPlanView } from 'mmfintech-backend-api/types'
import { GlobalContext, isValidArray, tr } from 'mmfintech-commons'
import { ErrorDisplay, Spinner } from 'mmfintech-portal-commons'
import { TRANSACTIONS_TABLE_SCROLL_CLASS } from '../TransactionsTable/TransactionsTable'
import RecurringDetailsModal from './RecurringDetailsModal'
import RecurringEditModal from './RecurringEditModal'
import RecurringPastTransactions from './RecurringPastTransactions'
import styles from './RecurringPayments.module.scss'
import RecurringPaymentsFilter from './RecurringPaymentsFilter'
import RecurringPaymentsTable from './RecurringPaymentsTable'

function RecurringPayments() {
  const { modalShow } = useContext(GlobalContext)
  const { filter, pagination, recurringPlans, recurringPlansError, recurringPlansFetching } = useRecurringPlansQry()
  const { hasAnyWritePermission } = useMyAccountAccessQry()
  const { accounts } = useMerchantAccounts()
  const navigate = useNavigate()
  const [planForHistory, setPlanForHistory] = useState<PortalRecurringPlanView>(null)

  // The wrapper hook's own autoApply/applyProc wiring doesn't reliably trigger a reload
  // on filter changes (confirmed against Kingdom's Recurring.tsx, which bypasses it and
  // reloads explicitly like this instead), so we watch the filter values ourselves.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reload only when filters change, not on every pagination identity change
  useEffect(() => {
    pagination.reload()
  }, [filter.filterValues.recurringStep, filter.filterValues.recurringPlanStatuses])

  function handleView(plan: PortalRecurringPlanView) {
    modalShow({ content: <RecurringDetailsModal plan={plan} /> })
  }

  function handleEdit(plan: PortalRecurringPlanView) {
    modalShow({ content: <RecurringEditModal plan={plan} reload={() => pagination.reload()} /> })
  }

  function handleCheckPastTransactions(plan: PortalRecurringPlanView) {
    setPlanForHistory(plan)
  }

  const canPayout = hasAnyWritePermission && isValidArray(accounts)
  const { recurringStep, recurringPlanStatuses } = filter.filterValues
  const hasFilters = [recurringStep, recurringPlanStatuses].some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  )

  return planForHistory ? (
    <RecurringPastTransactions plan={planForHistory} onBack={() => setPlanForHistory(null)} />
  ) : (
    <div
      className={cn(styles['recurring-payments'], 'recurring-payments-wrapper', PAGE_FILL.item)}
      data-test='recurring-payments-view'
    >
      <TableCard
        header={
          <>
            <RecurringPaymentsFilter filter={filter} />
            <ErrorDisplay error={recurringPlansError} />
          </>
        }
        fallback={
          recurringPlansFetching ? (
            <div className={cn(styles['recurring-payments-loading'], 'recurring-payments-loading')}>
              <Spinner />
            </div>
          ) : (
            !isValidArray(recurringPlans) &&
            (hasFilters ? (
              <EmptyState
                icon={<RecurringPaymentsIcon color='currentColor' size={48} />}
                title={tr('FRONTEND.RECURRING.EMPTY.NO_MATCH', 'No recurring payments match your filters')}
                data-test='recurring-payments-no-match'
              />
            ) : (
              <EmptyState
                icon={<RecurringPaymentsIcon color='currentColor' size={48} />}
                title={tr('FRONTEND.RECURRING.EMPTY.TITLE', 'No recurring payments')}
                message={tr(
                  'FRONTEND.RECURRING.EMPTY.HINT',
                  'Schedule one from any transfer by turning on "Schedule a recurring transfer".'
                )}
                action={
                  canPayout && (
                    <MMButton
                      variant='primary'
                      leftIcon={PayoutIcon}
                      caption={tr('FRONTEND.RECURRING.EMPTY.NEW_PAYOUT', 'New payout')}
                      onClick={() => navigate(paths.payout.select())}
                      data-test='button-new-payout'
                    />
                  )
                }
                data-test='recurring-payments-empty'
              />
            ))
          )
        }
        pagination={pagination.register()}
        scrollProps={{
          className: cn(TRANSACTIONS_TABLE_SCROLL_CLASS, 'table-cards-fit'),
          'data-test': 'recurring-payments-table'
        }}
      >
        <RecurringPaymentsTable
          recurringPlans={recurringPlans}
          onView={handleView}
          onEdit={handleEdit}
          onCheckPastTransactions={handleCheckPastTransactions}
        />
      </TableCard>
    </div>
  )
}

export default RecurringPayments
